
import { useEffect, useState } from "react";
import config from "../../../config/config";
import axios from "axios";
import { getAuthToken } from "../../../utils/token";

import ProductionDailyActivityList from "./ProductionDailyActivityList";
import ProductionDailyActivityModal from "./ProductionDailyActivityModal";
import ProductionDailyActivityDelete from "./ProductionDailyActivityDelete";



const ProductionDailyActivity = () => {

    // =========================================================
    // PRODUCTION ACTIVITIES
    // =========================================================

    const [activities, setActivities] = useState([]);

    const [loading, setLoading] = useState(false);

    const [error, setError] = useState("");


    // =========================================================
    // MASTER LISTS
    // =========================================================

    const [machines, setMachines] = useState([]);

    const [dies, setDies] = useState([]);

    const [products, setProducts] = useState([]);

    const [activityMasters, setActivityMasters] = useState([]);

    const [shifts] = useState([]);

    const [listLoading, setListLoading] = useState(false);


    // =========================================================
    // MODALS
    // =========================================================

    const [showModal, setShowModal] = useState(false);

    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const [selectedActivity, setSelectedActivity] = useState(null);


    // =========================================================
    // GET ALL PRODUCTION ACTIVITIES
    // =========================================================

    const fetchActivities = async () => {

        const token = getAuthToken();

        if (!token) return;

        try {

            setLoading(true);
            setError("");

            const response = await axios.get(
                `${config.API}/productiondailyactivity/index`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.data.success) {

                setActivities(
                    response.data.data || []
                );

            } else {

                setError(
                    response.data.message ||
                    "Unable to load activities."
                );

            }

        } catch (error) {

            console.error(
                "fetchActivities:",
                error
            );

            setError(
                error.response?.data?.message ||
                "Failed to load production activities."
            );

        } finally {

            setLoading(false);

        }
    };


    // =========================================================
    // GET MACHINE LIST
    // =========================================================

    const fetchMachineList = async () => {

        const token = getAuthToken();

        if (!token) return;

        try {

            const response = await axios.get(
                `${config.API}/machine/list`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            setMachines(
                response.data.machines || []
            );

        } catch (error) {

            console.error(
                "fetchMachineList:",
                error
            );

        }
    };


    // =========================================================
    // GET PRODUCT LIST
    // =========================================================

    const fetchProductList = async () => {

        const token = getAuthToken();

        if (!token) return;

        try {

            const response = await axios.get(
                `${config.API}/product/list`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            setProducts(
                response.data.products || []
            );

        } catch (error) {

            console.error(
                "fetchProductList:",
                error
            );

        }
    };


    // =========================================================
    // GET DIE LIST
    // =========================================================

    const fetchDieList = async () => {

        const token = getAuthToken();

        if (!token) return;

        try {

            const response = await axios.get(
                `${config.API}/admin/lisdie`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            setDies(
                response.data.dies || []
            );

        } catch (error) {

            console.error(
                "fetchDieList:",
                error
            );

        }
    };

const fetchMasterActivityList = async () => {
    const token = getAuthToken();

    if (!token) {
        console.error("Authentication token not found.");
        return;
    }

    try {
        setListLoading(true);

        const res = await axios.get(
            `${config.API}/activitymaster/fillMasterActivities`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: "application/json",
                },
            }
        );

        console.log("Activity Master Response:", res.data);

        if (res.data.success) {
            setActivityMasters(res.data.activities || []);
        } else {
            console.error(
                "Activity API error:",
                res.data.message
            );

            setActivityMasters([]);
        }

    } catch (err) {

        console.error(
            "fetchMasterActivityList:",
            err.response?.data || err
        );

        setActivityMasters([]);

    } finally {

        setListLoading(false);

    }
};


    // =========================================================
    // LOAD ALL DATA
    // =========================================================

    useEffect(() => {

        const loadMasterData = async () => {

            setListLoading(true);

            await Promise.all([
                fetchMachineList(),
                fetchProductList(),
                fetchDieList(),
                fetchMasterActivityList(),
            ]);

            setListLoading(false);

        };

        fetchActivities();

        loadMasterData();

    }, []);


    // =========================================================
    // ADD
    // =========================================================

    const handleAdd = () => {

        setSelectedActivity(null);

        setShowModal(true);

    };


    // =========================================================
    // EDIT
    // =========================================================

    const handleEdit = (activity) => {

        setSelectedActivity(activity);

        setShowModal(true);

    };


    // =========================================================
    // DELETE CLICK
    // =========================================================

    const handleDelete = (activity) => {

        setSelectedActivity(activity);

        setShowDeleteModal(true);

    };


    // =========================================================
    // CLOSE FORM MODAL
    // =========================================================

    const closeModal = () => {

        setShowModal(false);

        setSelectedActivity(null);

    };


    // =========================================================
    // CREATE
    // =========================================================

    const handleCreate = async (data) => {

        const token = getAuthToken();

        if (!token) return;

        try {

            setLoading(true);

            const response = await axios.post(
                `${config.API}/productiondailyactivity/create`,
                data,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.data.success) {

                setShowModal(false);

                setSelectedActivity(null);

                await fetchActivities();

            }

        } catch (error) {

            console.error(
                "handleCreate:",
                error
            );

            throw error;

        } finally {

            setLoading(false);

        }
    };


    // =========================================================
    // UPDATE
    // =========================================================

    const handleUpdate = async (id, data) => {

        const token = getAuthToken();

        if (!token) return;

        try {

            setLoading(true);

            const response = await axios.put(
                `${config.API}/productiondailyactivity/update/${id}`,
                data,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.data.success) {

                setShowModal(false);

                setSelectedActivity(null);

                await fetchActivities();

            }

        } catch (error) {

            console.error(
                "handleUpdate:",
                error
            );

            throw error;

        } finally {

            setLoading(false);

        }
    };


    // =========================================================
    // DELETE
    // =========================================================

    const handleConfirmDelete = async () => {

        const token = getAuthToken();

        if (!token) return;

        if (!selectedActivity) {
            return;
        }

        try {

            setLoading(true);

            const response = await axios.delete(
                `${config.API}/productiondailyactivity/delete/${selectedActivity.id}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.data.success) {

                setShowDeleteModal(false);

                setSelectedActivity(null);

                await fetchActivities();

            }

        } catch (error) {

            console.error(
                "handleConfirmDelete:",
                error
            );

            setError(
                error.response?.data?.message ||
                "Failed to delete activity."
            );

        } finally {

            setLoading(false);

        }
    };


    // =========================================================
    // RENDER
    // =========================================================

    return (

        <div className="container-fluid p-1">


            {/* =================================================
                PAGE HEADER
            ================================================= */}

            <div className="d-flex justify-content-between align-items-center mb-1">
              Manage Production Daily Activities
                <button
                    className="btn btn-primary"
                    onClick={handleAdd}
                >
                    + Add Process
                </button>

            </div>


            {/* =================================================
                ERROR
            ================================================= */}

            {error && (

                <div className="alert alert-danger">
                    {error}
                </div>

            )}


            {/* =================================================
                LIST
            ================================================= */}

            <ProductionDailyActivityList
                activities={activities}
                loading={loading}
                onEdit={handleEdit}
                onDelete={handleDelete}
            />


            {/* =================================================
                INSERT / UPDATE MODAL
            ================================================= */}

                    {showModal && (

                    <ProductionDailyActivityModal
                    activity={selectedActivity}
                    machines={machines}
                    dies={dies}
                    products={products}
                    activityMasters={activityMasters}
                    shifts={shifts}
                    listLoading={listLoading}
                    loading={loading}
                    onClose={closeModal}
                    onCreate={handleCreate}
                    onUpdate={handleUpdate}
                   />

                    )}


            {/* =================================================
                DELETE MODAL
            ================================================= */}

            {showDeleteModal && (

                <ProductionDailyActivityDelete

                    activity={selectedActivity}

                    loading={loading}

                    onClose={() => {

                        setShowDeleteModal(false);

                        setSelectedActivity(null);

                    }}

                    onConfirm={handleConfirmDelete}

                />

            )}

        </div>
    );
};


export default ProductionDailyActivity;

